<script lang="ts" setup>
import type { SidebarNavItem } from '~kui/components/KUI/Schools/Layout/Sidebar/types';

import { Button } from '~ds/components/ui/button';
import { Icon } from '~ds/components/ui/icon';
import { PageContainer } from '~ds/components/ui/page-container';
import { ICONS } from '~ds/lib/icons';

interface Props {
  navItems: SidebarNavItem[];
  loading?: boolean;
  logoHref: string;
  accountHref: string;
  schoolName?: string;
  dismissiblePromo?: boolean;
}

withDefaults(defineProps<Props>(), {
  loading: false,
  schoolName: undefined,
  dismissiblePromo: false,
});

const emit = defineEmits<{
  logout: [];
  dismissPromo: [];
}>();

const { t } = useI18n();
const configStore = useConfigStore();

const logoText = computed(() =>
  t('schools.nav.logoText', { productName: configStore.cluster?.manageProductName ?? '' }),
);

const { open: navOpen } = useSidebarOpenState('schools-nav-open');

const mainClasses = computed(() =>
  twMerge(
    cva('flex min-w-0 flex-1 flex-col transition-[margin] duration-200', {
      variants: {
        navOpen: { true: 'md:ml-[260px]', false: 'ml-0' },
      },
    })({ navOpen: navOpen.value }),
  ),
);
</script>

<template>
  <div class="flex min-h-dvh bg-background overflow-x-clip">
    <KUISchoolsLayoutSidebar
      v-model:is-open="navOpen"
      :nav-items="navItems"
      :loading="loading"
      :logo-href="logoHref"
      :account-href="accountHref"
      :school-name="schoolName"
      :account-label="t('schools.nav.myAccount')"
      :logout-label="t('schools.nav.logout')"
      :sidebar-label="t('schools.nav.sidebarLabel')"
      :primary-nav-label="t('schools.nav.primaryNavLabel')"
      :close-label="t('schools.nav.closeMenu')"
      :logo-text="logoText"
      :promo-dismiss-label="t('schools.nav.promoDismiss')"
      :dismissible-promo="dismissiblePromo"
      @logout="emit('logout')"
      @dismiss-promo="emit('dismissPromo')"
    >
      <template v-if="$slots.school" #school>
        <slot name="school" />
      </template>
      <template v-if="$slots.promo" #promo>
        <slot name="promo" />
      </template>
    </KUISchoolsLayoutSidebar>
    <div :class="mainClasses">
      <button
        v-if="!navOpen"
        type="button"
        class="hidden md:inline-flex fixed top-4 left-4 z-30 items-center justify-center p-2 rounded-full bg-white shadow hover:bg-accent/50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
        :aria-label="t('schools.nav.openMenu')"
        :aria-expanded="navOpen"
        data-testid="schools-desktop-menu-toggle"
        @click="navOpen = true"
      >
        <Icon :name="ICONS.menuSolid" size="sm" />
      </button>
      <div
        class="md:hidden sticky top-0 z-30 flex items-center justify-between border-b border-border bg-white px-4 py-3"
      >
        <Button
          variant="ghost"
          size="icon-sm"
          :aria-label="t('schools.nav.openMenu')"
          data-testid="schools-mobile-menu-toggle"
          @click="navOpen = true"
        >
          <Icon :name="ICONS.menuSolid" size="lg" />
        </Button>
        <NuxtLink
          :to="logoHref"
          class="min-w-0 truncate font-display text-base font-bold tracking-tight"
        >
          {{ logoText }}
        </NuxtLink>
        <span class="w-7" aria-hidden="true" />
      </div>
      <PageContainer
        id="main-content"
        as="main"
        size="xl"
        :gutter="false"
        class="flex-1 min-w-0 pt-8 pb-12 md:pt-12 md:pb-16"
      >
        <Suspense>
          <div class="container mx-auto">
            <slot v-if="$slots.plainContent" name="plainContent" />
            <div v-else-if="$slots.cardContent" class="px-4 sm:px-13 md:px-16">
              <KUICoreCard
                padding="none"
                elevation="none"
                rounding="3xl"
                class="py-8"
              >
                <slot name="cardContent" />
              </KUICoreCard>
            </div>
            <!-- Unnamed default slot renders unconditionally alongside plainContent/cardContent
                 (matching KUISchoolsLayout's sibling layout), so callers can pass extra content
                 (e.g. teleporting dialogs) regardless of which named slot they use. -->
            <slot />
          </div>
          <template #fallback>
            <KUICoreContainerCentered class="py-16">
              <KUICoreLoaderAnyway :text="$t('general.common.loading')" />
            </KUICoreContainerCentered>
          </template>
        </Suspense>
      </PageContainer>
    </div>
  </div>
</template>
